
import Vue, { PropType } from 'vue'
import * as d3 from 'd3'
// import * as d3Array from 'd3-array'
import { group } from 'd3-array'
import { ScaleSequential, HSLColor } from 'd3'

import { getYear, getUtcDate } from '@/helpers/dateHelpers'

declare module 'd3-array' {
  /**
   * Groups the specified array of values into a Map from key to array of value.
   * @param a The array to group.
   * @param key The key function.
   * @param key2 The key function.
   */
  export function group<TObject, TKey, TKey2>(
    a: Iterable<TObject>,
    key: (value: TObject) => TKey,
    key2: (value: TObject) => TKey2
  ): Map<TKey, Map<TKey2, TObject[]>>
}

// const minString = (values: string[]): string => d3.min(values)

const TODAY = new Date().toISOString().split('T')[0]

const timeWeek = d3.utcMonday
const countDay = (d: Date) => (d.getUTCDay() + 6) % 7

const formatValue = d3.format(',')
const formatDate = d3.utcFormat('%x')
const formatDay = (d: Date) => 'SMTWTFS'[d.getUTCDay()]
const formatMonth = d3.utcFormat('%b')

const ZERO_COLOR = 'rgba(239, 239, 239, 1)'

// export const interpolateHueLightness = (hue: number, saturation = 100, min = 50, max = 100) => (v: number) => `hsl(${Math.max(0, Math.min(360, hue))}, ${saturation}%, ${Math.min(100, max) - (v * (Math.min(100, max) - Math.max(0, min)))}%)`
export const interpolateHueLightness =
  (hue: number, saturation = 1, min = 0.5, max = 1) =>
  (v: number) =>
    d3.hsl(
      hue,
      saturation,
      d3.scaleLinear().domain([0, 1]).range([max, min])(v) ?? 0.5
    )

interface CalendarDataItem {
  date: string
  value: number
  type: string
}

type CalendarYearGroupItem = [number, CalendarDateGroupItem[]]
type CalendarDateGroupItem = [string, CalendarDataItem[]]
// type CalendarGroupItem  = [number, [Date, CalendarDataItem[]]]

type DateValues = [string, CalendarDataItem[]]

export default Vue.extend({
  props: {
    data: Array as PropType<Array<CalendarDataItem>>,

    cellSize: {
      type: Number,
      default: 17,
    },
    width: {
      type: Number,
      default: 954,
    },
  },
  data: () => ({
    svg: d3.select('*') as unknown as d3.Selection<
      Element,
      CalendarYearGroupItem[],
      null,
      undefined
    >, // <HTMLElement, {}, CalendarYearGroupItem, any>,
  }),
  computed: {
    years(): Array<CalendarYearGroupItem> {
      return Array.from(
        group(
          this.data,
          (d: CalendarDataItem) => getYear(d.date),
          (d: CalendarDataItem) => d.date
        )
      )
        .map(
          ([key, values]: [
            number,
            Map<string, CalendarDataItem[]>
          ]): CalendarYearGroupItem => [key, Array.from(values)]
        )
        .reverse()
    },
    height(): number {
      return this.cellSize * 9
    },
    color(): ScaleSequential<string | HSLColor> {
      const max = d3.max(
        this.years
          .map(([, values1]) =>
            values1.map(([, values2]) =>
              values2.reduce((tot, item) => tot + item.value, 0)
            )
          )
          .flat()
      )
      return d3
        .scaleSequential(interpolateHueLightness(0, 1, 0.5, 0.95))
        .domain([0, max || 0])
    },
  },
  watch: {
    data() {
      this.redraw()
    },
  },
  mounted() {
    this.draw()
  },
  methods: {
    pathMonth(t: Date) {
      const n = 7
      const d = Math.max(0, Math.min(n, countDay(t)))
      const w = timeWeek.count(d3.utcYear(t), t)
      return `${
        d === 0
          ? `M${w * this.cellSize},0`
          : d === n
          ? `M${(w + 1) * this.cellSize},0`
          : `M${(w + 1) * this.cellSize},0V${d * this.cellSize}H${
              w * this.cellSize
            }`
      }V${n * this.cellSize}`
    },
    redraw() {
      this.svg.selectAll('*').remove()

      this.drawData()
    },
    drawData() {
      const year = this.svg
        .selectAll('g')
        .data(this.years)
        .join('g')
        .attr(
          'transform',
          (d: CalendarYearGroupItem, i: number) =>
            `translate(40.5,${this.height * i + this.cellSize * 1.5})`
        )

      year
        .append('text')
        .attr('x', -5)
        .attr('y', -5)
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'end')
        .text(([key]) => key)

      year
        .append('g')
        .attr('text-anchor', 'end')
        .selectAll('text')
        .data(d3.range(7).map((i) => new Date(1995, 0, i)))
        .join('text')
        .attr('x', -5)
        .attr('y', (d) => (countDay(d) + 0.5) * this.cellSize)
        .attr('dy', '0.31em')
        .text(formatDay)

      year
        .append('g')
        .selectAll('rect')
        .data(([, values]) => values)
        .join('rect')
        .attr('width', this.cellSize - 1)
        .attr('height', this.cellSize - 1)
        .attr(
          'x',
          ([d]) =>
            timeWeek.count(d3.utcYear(getUtcDate(d)), getUtcDate(d)) *
              this.cellSize +
            0.5
        )
        .attr('y', ([d]) => countDay(getUtcDate(d)) * this.cellSize + 0.5)
        // .attr("fill", d => this.color(d.value))
        .attr('fill', ([, values]: CalendarDateGroupItem): string => {
          const total = values.reduce(
            (acc: number, v: CalendarDataItem) => acc + v.value,
            0
          )
          return total <= 0
            ? ZERO_COLOR
            : this.color(total)?.toString() ?? ZERO_COLOR
        })
        // .attr('stroke', ([key]) => key === TODAY ? 'blue' : 'none')
        // .attr('stroke-width', 1)
        .append('title')
        .text(
          ([d, values]: CalendarDateGroupItem) => `
            ${formatDate(getUtcDate(d))}
            ${[
              ...new Set(
                values
                  .map((v: CalendarDataItem) => v.type)
                  .filter((v) => v !== 'none')
              ),
            ].join(', ')}: ${formatValue(
            values.reduce(
              (acc: number, v: CalendarDataItem) => acc + v.value,
              0
            )
          )}
          `
        )

      const month = year
        .append('g')
        .selectAll('g')
        .data(([, values]) =>
          d3.utcMonths(
            d3.utcMonth(getUtcDate(d3.min(values.map(([d]) => d)) || TODAY)),
            getUtcDate(d3.max(values.map(([d]) => d)) || TODAY)
          )
        )
        .join('g')

      month
        .filter((d: Date, i: number) => !!i)
        .append('path')
        .attr('fill', 'none')
        .attr('stroke', '#fff')
        .attr('stroke-width', 3)
        .attr('d', this.pathMonth)

      month
        .append('text')
        .attr(
          'x',
          (d) =>
            timeWeek.count(d3.utcYear(d), timeWeek.ceil(d)) * this.cellSize + 2
        )
        .attr('y', -5)
        .text(formatMonth)
    },
    draw() {
      if (!this.$refs.svg) throw new Error('no svg')
      // eslint-ignore-next-line
      this.svg = d3
        .select<Element, CalendarYearGroupItem[]>(this.$refs.svg as Element)
        .attr(
          'viewBox',
          `0, 0, ${this.width}, ${this.height * this.years.length}`
        )
        .attr('font-family', 'sans-serif')
        .attr('font-size', 10)

      this.drawData()

      return this.svg.node()
    },
  },
})
